import React, { useState } from 'react';
import { FileText, Download, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useSettings } from '../contexts/SettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { LeadCapturePopup } from './LeadCapturePopup';
import { ThankYouOverlay } from './ThankYouOverlay';
import { cardImage } from '../utils/imageOptimizer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}/${url}`;
};

export const BrochureSection = () => {
  const { settings } = useSettings();
  const { t, getBilingualValue } = useLanguage();
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [capturedContactId, setCapturedContactId] = useState(null);
  const [capturedProfile, setCapturedProfile] = useState(null);
  
  const primaryColor = settings.colors?.primary || '#0E7A5F';
  const hasBrochure = !!settings.brochure?.file;
  const brochureDownloadUrl = `${BACKEND_URL}/api/brochure/download`;
  const sponsorshipUrl = getFullUrl(settings.brochure?.sponsorshipFile);
  const thumbnailUrl = getFullUrl(settings.brochure?.thumbnail);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) { window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' }); }
  };

  const handleDownloadBrochure = () => {
    if (hasBrochure) {
      setShowLeadPopup(true);
    } else {
      toast.info('Brochure will be available soon. Please contact us for more information.');
    }
  };

  const handleLeadSuccess = (contactId, profile) => {
    setCapturedContactId(contactId);
    setCapturedProfile(profile || null);
    setShowLeadPopup(false);
    // Short delay then show thank you overlay
    setTimeout(() => setShowThankYou(true), 600);
  };

  const handleDownloadSponsorship = () => {
    if (sponsorshipUrl) {
      window.open(sponsorshipUrl, '_blank');
    } else {
      scrollToSection('contact');
    }
  };

  const title = getBilingualValue(settings.brochure?.title) || t('brochure.title');
  const description = getBilingualValue(settings.brochure?.description) || t('brochure.description');
  const buttonText = getBilingualValue(settings.brochure?.button) || t('brochure.download');
  const buttonAltText = getBilingualValue(settings.brochure?.buttonAlt) || t('brochure.requestDeck');
  const note = getBilingualValue(settings.brochure?.note) || t('brochure.note');

  return (
    <>
      <section id="brochure" className="bg-gray-50 py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="md:flex">
              <div className="md:w-2/5 bg-gradient-to-br from-green-100 to-teal-200 p-8 flex items-center justify-center">
                {thumbnailUrl ? (
                  <img 
                    src={cardImage(thumbnailUrl)} alt="Brochure Preview" 
                    className="w-full h-auto max-h-64 object-contain rounded-lg shadow-md cursor-pointer hover:scale-105 transition-transform"
                    onClick={handleDownloadBrochure}
                  />
                ) : (
                  <FileText size={120} style={{ color: primaryColor }} strokeWidth={1.5} />
                )}
              </div>
              <div className="md:w-3/5 p-12">
                <h2 className="text-4xl font-bold text-gray-900 mb-6">{title}</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">{description}</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className={`text-white shadow-lg flex items-center gap-2 ${!hasBrochure ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ backgroundColor: primaryColor }}
                    onMouseEnter={(e) => hasBrochure && (e.currentTarget.style.backgroundColor = settings.colors?.primaryHover || '#0A5F4A')}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
                    onClick={handleDownloadBrochure}
                    disabled={!hasBrochure}
                    data-testid="brochure-download-btn"
                  >
                    <Download size={18} /> {buttonText}
                  </Button>
                  <Button
                    size="lg" variant="outline"
                    style={{ borderColor: primaryColor, color: primaryColor }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${primaryColor}10`}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={handleDownloadSponsorship}
                  >
                    {buttonAltText}
                  </Button>
                </div>
                
                {!hasBrochure && (
                  <div className="flex items-center gap-2 mt-4 text-amber-600 text-sm">
                    <AlertCircle size={16} />
                    <span>{t('brochure.comingSoon')}</span>
                  </div>
                )}
                
                <p className="text-sm text-gray-500 mt-6">{note}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture Popup */}
      <LeadCapturePopup
        open={showLeadPopup}
        onClose={() => setShowLeadPopup(false)}
        resourceName="Event Brochure"
        downloadUrl={brochureDownloadUrl}
        onSuccess={handleLeadSuccess}
      />

      {/* Thank You Overlay */}
      <ThankYouOverlay
        open={showThankYou}
        onClose={() => setShowThankYou(false)}
        contactId={capturedContactId}
        profile={capturedProfile}
      />
    </>
  );
};
